// Product scanner — extracts purchasable products from the current page DOM.
// Tries JSON-LD first, then falls back to DOM heuristics, then to a sensible default.

export interface ScannedProduct {
  title: string
  editionLabel: string
  priceNumber: number
  priceText: string
  thumbnailUrl: string
}

// Hard-coded fallback that matches what's actually on this landing page.
// Used only if DOM scanning yields nothing usable.
const FALLBACK_PRODUCTS: ScannedProduct[] = [
  {
    title: 'Arte con Cemento',
    editionLabel: 'Programa Completo',
    priceNumber: 17999,
    priceText: '$17.999 ARS',
    thumbnailUrl:
      'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/ebook_principal.png?v=1779992128',
  },
  {
    title: 'Arte con Cemento',
    editionLabel: 'Bundle + Moldes de Silicona',
    priceNumber: 27989,
    priceText: '$27.989 ARS',
    thumbnailUrl:
      'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/bundle.png?v=1779992163',
  },
]

// Distribution weights (parallel to FALLBACK_PRODUCTS). Main offer 70%, bundle 30%.
const FALLBACK_WEIGHTS = [0.7, 0.3]

/**
 * Format a number as Argentine pesos: "$17.999 ARS"
 */
function formatARS(n: number): string {
  return `$${n.toLocaleString('es-AR')} ARS`
}

/**
 * Parse a price string like "$17.999 ARS", "27.989", "$27 USD" into a number.
 * Returns NaN if no number is found.
 */
function parsePriceString(raw: string): number {
  if (!raw) return NaN
  // Remove currency symbols + letters, keep digits + dots + commas
  const cleaned = raw.replace(/[^\d.,]/g, '')
  if (!cleaned) return NaN
  // Argentine format uses dots as thousands separator: "17.999" → 17999
  // If only dots and no comma, treat dots as thousands separators
  if (cleaned.includes('.') && !cleaned.includes(',')) {
    const stripped = cleaned.replace(/\./g, '')
    const n = parseInt(stripped, 10)
    return isNaN(n) ? NaN : n
  }
  // Otherwise parseFloat (handles "17,99" or "1799.99")
  const n = parseFloat(cleaned.replace(/,/g, '.'))
  return isNaN(n) ? NaN : n
}

/**
 * Try to extract products from JSON-LD <script type="application/ld+json"> blocks.
 */
function scanJsonLd(): ScannedProduct[] {
  const products: ScannedProduct[] = []
  try {
    const scripts = document.querySelectorAll<HTMLScriptElement>(
      'script[type="application/ld+json"]',
    )
    scripts.forEach((s) => {
      try {
        const data = JSON.parse(s.textContent || '')
        const items = Array.isArray(data) ? data : [data]
        items.forEach((item: any) => {
          if (!item || item['@type'] !== 'Product') return
          const offers = Array.isArray(item.offers)
            ? item.offers
            : [item.offers]
          offers.forEach((offer: any) => {
            const priceNumber = parsePriceString(String(offer?.price ?? ''))
            if (isNaN(priceNumber)) return
            products.push({
              title: item.name || 'Producto',
              editionLabel: offer?.name || offer?.sku || '',
              priceNumber,
              priceText: `$${priceNumber.toLocaleString('es-AR')} ${
                offer?.priceCurrency || 'ARS'
              }`,
              thumbnailUrl: Array.isArray(item.image)
                ? item.image[0]
                : item.image,
            })
          })
        })
      } catch {
        /* ignore individual JSON-LD parse errors */
      }
    })
  } catch {
    /* ignore */
  }
  return products
}

/**
 * Look for an element with id="oferta" (the main Offer section on this page)
 * and extract product info from it.
 */
function scanOfferSection(): ScannedProduct[] {
  const offer = document.getElementById('oferta')
  if (!offer) return []

  // Find the largest price-looking text inside #oferta
  const priceRegex = /\$\s?\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{2})?/g
  const text = offer.textContent || ''
  const matches = Array.from(text.matchAll(priceRegex)).map((m) => m[0])
  if (matches.length === 0) return []

  // Convert to numbers, ignore the anchor "$39.990" (clearly a strike-through),
  // take the largest realistic value as the bundle and the next biggest as main.
  const nums = matches
    .map(parsePriceString)
    .filter((n) => !isNaN(n) && n > 100)
    // de-dupe
    .filter((n, i, arr) => arr.indexOf(n) === i)
    .sort((a, b) => a - b)

  if (nums.length === 0) return []

  // Find any product image inside #oferta as thumbnail
  const img = offer.querySelector<HTMLImageElement>('img[src*="cdn.shopify"]')
  const thumb =
    img?.src ||
    'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/ebook_principal.png?v=1779992128'

  // Use the two smallest values as "main" and "bundle"
  // (largest one is usually the strike-through anchor price)
  const scanned: ScannedProduct[] = []
  if (nums.length >= 1) {
    scanned.push({
      title: 'Arte con Cemento',
      editionLabel: 'Programa Completo',
      priceNumber: nums[0],
      priceText: formatARS(nums[0]),
      thumbnailUrl: thumb,
    })
  }
  if (nums.length >= 2) {
    scanned.push({
      title: 'Arte con Cemento',
      editionLabel: 'Bundle + Moldes de Silicona',
      priceNumber: nums[1],
      priceText: formatARS(nums[1]),
      thumbnailUrl:
        'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/bundle.png?v=1779992163',
    })
  }
  return scanned
}

/**
 * Main entry: returns a weighted catalog of products discovered on the page.
 * Each product has an attached weight so callers can do weighted random selection.
 */
export interface CatalogEntry {
  product: ScannedProduct
  weight: number
}

export function scanProducts(): CatalogEntry[] {
  // 1. Try JSON-LD
  const jsonLd = scanJsonLd()
  if (jsonLd.length > 0) {
    // Even weights
    return jsonLd.map((p) => ({ product: p, weight: 1 / jsonLd.length }))
  }

  // 2. Try Offer section heuristic
  const offerScan = scanOfferSection()
  if (offerScan.length > 0) {
    // Heuristic: assume the lower-priced item sells ~70% of the time
    if (offerScan.length === 1) {
      return [{ product: offerScan[0], weight: 1 }]
    }
    return [
      { product: offerScan[0], weight: 0.7 },
      { product: offerScan[1], weight: 0.3 },
    ]
  }

  // 3. Fallback hard-coded catalog (matches what's on the page anyway)
  return FALLBACK_PRODUCTS.map((p, i) => ({
    product: p,
    weight: FALLBACK_WEIGHTS[i] ?? 1 / FALLBACK_PRODUCTS.length,
  }))
}

/**
 * Pick one product from the catalog using weighted random selection.
 */
export function pickProduct(catalog: CatalogEntry[]): ScannedProduct {
  if (catalog.length === 0) return FALLBACK_PRODUCTS[0]
  const total = catalog.reduce((sum, e) => sum + e.weight, 0)
  let r = Math.random() * total
  for (const entry of catalog) {
    r -= entry.weight
    if (r <= 0) return entry.product
  }
  return catalog[catalog.length - 1].product
}
